import { DatePipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Router } from '@angular/router';
import { ServiceRequestFlagsSignalRInterface } from '@interfaces/singnal-r/service-request-flags-signal-r.interface';
import { ServiceRequestFlagsComponent } from '@modals/service-request-flags/component/service-request-flags.component';
import { NotificationComponent } from '@modules/notification-queue/abstract/notification.component';
import { NotificationQueueHttpService } from '@modules/notification-queue/services/notification-queue-http.service';
import { first } from 'rxjs';
import { Constant } from 'src/app/@shared/constants/Constant';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { setCategoryColor } from '@helpers/service-request-flag.helper';
import { DsNotificationComponent } from '@design-system/ds-notification/component/ds-notification.component';

@Component({
  selector: 'app-service-request-flag-notification',
  templateUrl: './service-request-flag-notification.component.html',
  styleUrls: ['./service-request-flag-notification.component.scss'],
  standalone: true,
  imports: [DatePipe, NgIf, MatDialogModule, DsNotificationComponent],
  providers: [NotificationQueueHttpService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestFlagNotificationComponent extends NotificationComponent implements OnChanges {
  @Input() override notification: ServiceRequestFlagsSignalRInterface | null = null;

  timeZone: string = Constant.selectedTimeZoneShortValue;

  constructor(
    router: Router,
    private readonly notificationQueueHttpService: NotificationQueueHttpService,
    private readonly serviceRequestService: ServiceRequestService,
    private _dialog: MatDialog,
  ) {
    super(router);

    this.serviceRequestService.timZoneChange.subscribe(() => {
      this.timeZone = Constant.selectedTimeZoneShortValue;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notification']) {
      const currentFollowUpTime = new Date(this.notification?.FollowUpTime);
      this.notification = {
        ...this.notification,
        FollowUpTime: new Date(currentFollowUpTime.getTime() - currentFollowUpTime.getTimezoneOffset() * 60000),
        categoryColor: setCategoryColor(this.notification?.Category),
      };
    }
  }

  /**
   * Navigates to the service-request list page with specified query param
   */
  override async onViewServiceRequest(): Promise<void> {
    const data = {
      timezone: this.timeZone.replace(/\(.*?\).*/, '').trim(),
      serviceRequestId: this.notification.ServiceRequestId,
      customerName: this.notification.CustomerName,
      tripID: this.notification.TripId,
      serviceType: this.notification.ServiceType,
      serviceSubType: this.notification.ServiceSubType,
      status: this.notification.Status,
      notificationId: this.notification.notificationId,
    };
    const dialogRef = this._dialog.open(ServiceRequestFlagsComponent, {
      maxWidth: '80rem',
      width: '100%',
      minWidth: '21rem',
      height: '80vh',
      panelClass: 'service-request-flags-modal',
      data: data,
    });
    dialogRef.afterClosed().subscribe((notificationId: string) => {
      this.serviceRequestService.triggerRefresh();

      if (notificationId) {
        this.closeNotification();
      }
    });
  }

  /**
   * Send API call to dismiss internal trip notification and close the notification window
   */
  override closeNotification(): void {
    const notificationId = this.notification.notificationId;
    if (!this.notification.isFlagNotification) {
      this.notificationQueueHttpService.dismissNotification(notificationId).pipe(first()).subscribe();
    }
    super.closeNotification(notificationId);
  }
}
